import { useCallback, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../app/modules/auth";
import { sendChatMessage, ChatIdentity } from "../../models/_chat";

export interface ChatMessage {
  id: string;
  who: "user" | "bot";
  text: string;
  pending?: boolean;
  error?: boolean;
}

const THREAD_ID_KEY = "portalv2_chat_thread_id";

function getOrCreateThreadId(): string {
  let id = localStorage.getItem(THREAD_ID_KEY);
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
    localStorage.setItem(THREAD_ID_KEY, id);
  }
  return id;
}

function speak(text: string, onEnd?: () => void) {
  if (!("speechSynthesis" in window)) {
    onEnd?.();
    return;
  }
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  if (onEnd) utter.addEventListener("end", onEnd);
  window.speechSynthesis.speak(utter);
}

export function useChatWidget() {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      who: "bot",
      text: "Hi — ask me about locations, machines, tickets, or anything else in portalv2. I'll ask for confirmation before making any changes.",
    },
  ]);
  const [sending, setSending] = useState(false);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Bumped after speaking a reply that came from a voice message, so ChatPopup's
  // effect knows to auto-resume listening — enables a hands-free back-and-forth
  // instead of requiring a mic click for every turn.
  const [autoListenSignal, setAutoListenSignal] = useState(0);
  const threadId = useRef(getOrCreateThreadId());

  const identity: ChatIdentity = useMemo(
    () => ({
      name: [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(" ") || undefined,
      email: currentUser?.email,
    }),
    [currentUser],
  );

  const toggleOpen = useCallback(() => {
    setOpen((prev) => {
      if (!prev) setUnreadCount(0);
      return !prev;
    });
  }, []);

  const sendMessage = useCallback(
    async (text: string, viaVoice = false) => {
      const trimmed = text.trim();
      if (!trimmed || sending) return;

      setMessages((prev) => [...prev, { id: crypto.randomUUID(), who: "user", text: trimmed }]);
      setSending(true);

      try {
        const res = await sendChatMessage(threadId.current, identity, trimmed);
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), who: "bot", text: res.reply, pending: res.pendingConfirmation, error: Boolean(res.error) },
        ]);
        setAwaitingConfirmation(res.pendingConfirmation);
        setOpen((isOpen) => {
          if (!isOpen) setUnreadCount((c) => c + 1);
          return isOpen;
        });
        // Only auto-continue the hands-free loop for genuinely spoken turns,
        // and not after a network/tool error — an error reply looping back
        // into listening again would be a confusing, hard-to-escape UX.
        speak(res.reply, () => {
          if (viaVoice && !res.error) setAutoListenSignal((n) => n + 1);
        });
      } catch (e: any) {
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), who: "bot", text: `Network error: ${e.message}`, error: true },
        ]);
      } finally {
        setSending(false);
      }
    },
    [identity, sending],
  );

  return { open, toggleOpen, messages, sending, awaitingConfirmation, unreadCount, autoListenSignal, sendMessage };
}
